import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, Save, X, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

interface EducationContent {
  id: string;
  tool_name: string;
  category: string;
  short_description: string;
  why_it_matters: string;
  research_summary: string;
  how_to_optimize?: string;
  research_links: any; // Can be JSONB from database
  tags: string[];
  is_published: boolean;
}

export default function WellnessEducationManager() {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<EducationContent>>({
    tool_name: '',
    category: '',
    short_description: '',
    why_it_matters: '',
    research_summary: '',
    how_to_optimize: '',
    research_links: [],
    tags: [],
    is_published: true,
  });

  const queryClient = useQueryClient();

  const { data: educationContent, isLoading } = useQuery({
    queryKey: ['wellness-education'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wellness_education')
        .select('*')
        .order('tool_name');

      if (error) throw error;
      return data as EducationContent[];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (content: Partial<EducationContent>) => {
      const { data, error } = await supabase
        .from('wellness_education')
        .insert([content as any])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wellness-education'] });
      toast.success('Education content created');
      resetForm();
    },
    onError: (error) => {
      toast.error(`Failed to create: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<EducationContent> }) => {
      const { data, error } = await supabase
        .from('wellness_education')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wellness-education'] });
      toast.success('Education content updated');
      resetForm();
    },
    onError: (error) => {
      toast.error(`Failed to update: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('wellness_education')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wellness-education'] });
      toast.success('Education content deleted');
    },
    onError: (error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });

  const resetForm = () => {
    setFormData({
      tool_name: '',
      category: '',
      short_description: '',
      why_it_matters: '',
      research_summary: '',
      how_to_optimize: '',
      research_links: [],
      tags: [],
      is_published: true,
    });
    setIsAdding(false);
    setEditingId(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      updateMutation.mutate({ id: editingId, updates: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const startEdit = (content: EducationContent) => {
    setFormData(content);
    setEditingId(content.id);
    setIsAdding(true);
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading education content...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="w-6 h-6" />
            Wellness Education Manager
          </h2>
          <p className="text-muted-foreground mt-1">
            Manage psychoeducation content for wellness tools
          </p>
        </div>
        {!isAdding && (
          <Button onClick={() => setIsAdding(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Content
          </Button>
        )}
      </div>

      {isAdding && (
        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="tool_name">Tool Name *</Label>
                <Input
                  id="tool_name"
                  value={formData.tool_name}
                  onChange={(e) => setFormData({ ...formData, tool_name: e.target.value })}
                  placeholder="e.g., water_tracking"
                  required
                />
              </div>
              <div>
                <Label htmlFor="category">Category *</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="e.g., hydration"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="short_description">Short Description *</Label>
              <Textarea
                id="short_description"
                value={formData.short_description}
                onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                placeholder="Brief overview (1-2 sentences)"
                required
              />
            </div>

            <div>
              <Label htmlFor="why_it_matters">Why It Matters *</Label>
              <Textarea
                id="why_it_matters"
                value={formData.why_it_matters}
                onChange={(e) => setFormData({ ...formData, why_it_matters: e.target.value })}
                placeholder="Explain the importance and impact"
                rows={3}
                required
              />
            </div>

            <div>
              <Label htmlFor="research_summary">Research Summary *</Label>
              <Textarea
                id="research_summary"
                value={formData.research_summary}
                onChange={(e) => setFormData({ ...formData, research_summary: e.target.value })}
                placeholder="Summarise the research findings"
                rows={3}
                required
              />
            </div>

            <div>
              <Label htmlFor="how_to_optimize">How to Optimise (Optional)</Label>
              <Textarea
                id="how_to_optimize"
                value={formData.how_to_optimize || ''}
                onChange={(e) => setFormData({ ...formData, how_to_optimize: e.target.value })}
                placeholder="Practical tips for maximising benefits"
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input
                id="tags"
                value={formData.tags?.join(', ') || ''}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value.split(',').map(t => t.trim()) })}
                placeholder="e.g., sleep, mood, cognitive-function"
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="is_published"
                checked={formData.is_published}
                onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
              />
              <Label htmlFor="is_published">Published</Label>
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                <Save className="w-4 h-4 mr-2" />
                {editingId ? 'Update' : 'Create'}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      <div className="grid gap-4">
        {educationContent?.map((content) => (
          <Card key={content.id} className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{content.tool_name}</h3>
                  <Badge variant="secondary">{content.category}</Badge>
                  {!content.is_published && <Badge variant="outline">Draft</Badge>}
                </div>
                <p className="text-sm text-muted-foreground">{content.short_description}</p>
                {content.tags && content.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {content.tags.map(tag => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => startEdit(content)}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => {
                    if (confirm('Delete this education content?')) {
                      deleteMutation.mutate(content.id);
                    }
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
